<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import AppUpdateDrawer from '@/components/AppUpdateDrawer.vue';
import { performHubLogout, performHubUnauthorizedSession } from '@/auth/logout';
import { useAppUpdateCheck } from '@/composables/useAppUpdateCheck';
import { useHubSettingsDrawer } from '@/composables/useHubSettingsDrawer';
import { useLaunchFailureRecovery } from '@/composables/useLaunchFailureRecovery';
import { useNativeAppVersion } from '@/composables/useNativeAppVersion';
import { useSlowLaunchFeedback } from '@/composables/useSlowLaunchFeedback';
import { setLastHubAppId } from '@/shell/lastHubApp';
import { shellOrchestrator } from '@/shell/orchestrator';
import { useHubAuthStore } from '@/store/hubAuthStore';
import type { ShellContext } from '@apexx/shell-contract';
import { Button, Typography } from '@apexx/component-library';

;

const route = useRoute();
const router = useRouter();
const authStore = useHubAuthStore();
const containerRef = ref<HTMLElement | null>(null);
const isLaunching = ref(false);

const appId = computed(() => String(route.params.appId ?? ''));
const hubSubPath = computed(() => {
	const raw = route.params.pathMatch;
	return Array.isArray(raw) ? raw.join('/') : String(raw ?? '');
});
const { appVersion } = useNativeAppVersion();
const {
	drawerOpen: updateDrawerOpen,
	currentDisplayVersion,
	availableDisplayVersion,
	openAppStore,
	dismissUpdate,
} = useAppUpdateCheck();
const { showSlowMessage, showRefreshLink, refreshApp } = useSlowLaunchFeedback(
	isLaunching,
	appId,
);
const { openSettingsDrawer } = useHubSettingsDrawer();

function navigateToWelcome(): void {
	void shellOrchestrator.unmountActive();
	void router.replace({ name: 'launcher' });
}

const {
	launchError,
	recordSuccess,
	recordFailure,
	refreshLaunch,
	recoverToWelcome,
} = useLaunchFailureRecovery({
	appId,
	appVersion,
	onRecoverToWelcome: navigateToWelcome,
});

async function launchCurrentApp(): Promise<void> {
	if (!containerRef.value || !authStore.accessToken) {
		return;
	}

	// Token refresh re-fires this watcher; keep the mounted UI visible (no loading flash).
	const alreadyMounted = shellOrchestrator.isActiveInContainer(
		appId.value,
		containerRef.value,
	);
	if (!alreadyMounted) {
		isLaunching.value = true;
	}
	launchError.value = undefined;

	const ctx: ShellContext = {
		accessToken: authStore.accessToken,
		appVersion: appVersion.value,
		navigate: (path) => {
			void router.push(path);
		},
		getContainer: () => containerRef.value!,
		handleUnauthorized: () =>
			performHubUnauthorizedSession(router, 'api-unauthorized'),
		handleSignOut: () =>
			performHubLogout(router, { reason: 'user-sign-out' }),
		openSettings: openSettingsDrawer,
	};

	try {
		const outcome = await shellOrchestrator.launchApp(
			appId.value,
			containerRef.value,
			ctx,
		);
		// Path sync after token-only propagate would reset in-app routes (e.g. channel → chats)
		// when hub pathMatch is still empty. Real hub URL changes use the hubSubPath watcher.
		if (outcome === 'mounted') {
			await shellOrchestrator.syncActiveSubPath(
				appId.value,
				hubSubPath.value,
			);
		}
		if (outcome === 'mounted' || outcome === 'propagated') {
			void setLastHubAppId(appId.value);
		}
		recordSuccess();
	} catch (error) {
		recordFailure(error);
	} finally {
		isLaunching.value = false;
	}
}

function softRefreshLaunch(): void {
	window.location.reload();
}

// Do not watch `appVersion`: it resolves async via Capacitor after mount. Including it
// remounts the sub-app mid-patch (nextSibling null) once getInfo() returns.
watch(
	[appId, () => authStore.accessToken, containerRef],
	() => {
		void launchCurrentApp();
	},
	{ immediate: true },
);

// When the hub URL changes while the same app stays mounted (e.g. warm-start share →
// /apps/chat/share), push the sub-path into the embedded router. Remount is skipped for
// same-app launches, so without this the UI would stay on the previous page.
watch(hubSubPath, (subPath) => {
	if (!appId.value) {
		return;
	}

	void shellOrchestrator.syncActiveSubPath(appId.value, subPath);
});

onBeforeUnmount(() => {
	void shellOrchestrator.unmountActive();
});
</script>

<template>
	<div class="flex h-dvh min-h-0 flex-col bg-surface">
		<!-- The embedded sub-app owns all visible chrome (sidebar/topbar); the hub renders
			 no TopBar here. Loading/error states are shown only until the sub-app mounts. -->
		<div
			v-if="launchError"
			class="flex flex-1 flex-col items-center justify-center p-6 text-center"
			role="alert"
		>
			<div class="flex w-full max-w-xs flex-col items-center">
				<Typography variant="headline" class="text-on-surface" as="h1">
					Something went wrong
				</Typography>
				<Typography
					variant="body"
					class="mt-3 text-on-surface-muted"
					as="p"
				>
					You can try again, or go back to the home screen.
				</Typography>
				<Typography
					variant="body"
					class="mt-2 text-sm text-on-surface-muted/80"
					as="p"
				>
					{{ launchError }}
				</Typography>
				<div class="mt-6 flex w-full flex-col gap-3">
					<Button
						variant="primary"
						class="w-full"
						@click="softRefreshLaunch"
					>
						Refresh
					</Button>
					<Button
						variant="outline"
						class="w-full"
						@click="recoverToWelcome"
					>
						Home Screen
					</Button>
				</div>
			</div>
		</div>

		<div
			v-else-if="isLaunching"
			class="flex flex-1 flex-col items-center justify-center p-6 text-center"
			role="status"
			aria-live="polite"
			aria-busy="true"
		>
			<!-- Fixed-size card: status/refresh rows are always reserved so nothing reflows. -->
			<div class="flex h-52 w-full max-w-xs flex-col items-center">
				<div
					class="h-9 w-9 shrink-0 animate-spin rounded-full border-2 border-primary/20 border-t-primary"
					aria-hidden="true"
				/>
				<Typography
					variant="body"
					class="mt-4 h-6 shrink-0 animate-pulse text-on-surface-muted"
					as="p"
				>
					Loading app
					<span
						class="inline-flex w-[1.25em] justify-start overflow-hidden"
						aria-hidden="true"
					>
						<span class="animate-hub-launch-dots">...</span>
					</span>
				</Typography>

				<div
					class="mt-3 grid w-full shrink-0 grid-rows-[2.75rem_1.5rem] gap-2"
				>
					<p
						class="flex h-full items-center justify-center px-1 text-body leading-snug text-on-surface-muted"
					>
						<span
							v-if="showSlowMessage || showRefreshLink"
							class="animate-hub-launch-fade-in"
						>
							{{
								showRefreshLink
									? 'We may be experiencing issues. Please try again.'
									: 'Taking longer than usual…'
							}}
						</span>
						<span v-else class="invisible" aria-hidden="true">
							We may be experiencing issues. Please try again.
						</span>
					</p>
					<div class="flex h-full items-center justify-center">
						<button
							v-if="showRefreshLink"
							type="button"
							class="animate-hub-launch-fade-in text-body font-medium text-primary underline underline-offset-2"
							@click="refreshApp"
						>
							Refresh App
						</button>
						<span
							v-else
							class="invisible text-body font-medium underline"
							aria-hidden="true"
						>
							Refresh App
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Sub-app mount point: flex column + h-full so embedded apps inherit a definite
			 height for page transitions (percentage transforms) instead of viewport units. -->
		<div
			ref="containerRef"
			class="relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
		/>

		<AppUpdateDrawer
			v-model:open="updateDrawerOpen"
			:current-display-version="currentDisplayVersion"
			:available-display-version="availableDisplayVersion"
			@update="openAppStore"
			@dismiss="dismissUpdate"
		/>
	</div>
</template>
